import { groupBy, isEqual, sumBy } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useMemo, useState } from 'react';
import uuid from 'react-uuid';
import { PreCheckResult } from 'src/components/design-system/Button';
import useAsync from 'src/hooks/useAsync';
import {
  IInvoiceBudgetPeriod,
  InvoiceBudgetPeriodResponse,
  generateInitialAllocations,
  updateAllocationDecimal,
} from 'src/sections/invoice_details/InvoiceDetailsContext';
import { calculateTotals } from 'src/sections/invoice_details/useGlDistributionsGrandTotals';
import { calculatePercentage } from 'src/utils';
import axiosInstance from 'src/utils/axios';
import { removeTime } from 'src/utils/formatTime';
import { round } from 'src/utils/number';
import { InvoiceGlBreakoutItem } from './useInvoiceGlBreakoutApi';
import { InvoiceLineItem } from './useInvoiceLineItemsApi';

export const InvoiceTrigger = [
  'Startup',
  'SubmitForApproval',
  'Delete',
  'Undelete',
  'Cancel',
  'Recall',
  'Approve',
  'Complete',
  'Decline',
  'ChangePO',
  'Update',
  'AddLineItem',
  'SplitLineItem',
] as const;

export interface SupplierInfo {
  companyName: string;
  name: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  phone: string;
  fax: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface BuyerInfo {
  companyName: string;
  name: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  phone: string;
  fax: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface InvoiceAttachment {
  attachId: number;
  companyId: string | null;
  companyName: string | null;
  createdBy: string;
  creationDate: string;
  description: string;
  originalFile: string;
  receiptId: number;
  signedUrl: string;
}

export interface InvoiceDetailsResponse {
  invoiceNumber: string;
  receiptId: number;
  companyId: number;
  ocrGenerated: boolean;
  receivingReceiptId: number | null;
  glPeriodId: number;
  poId: number;
  poNum: string;
  clientId: string;
  invoiceDate: Date | null;
  _invoiceDate: string;
  receivedDate: Date | null;
  _receivedDate: string;
  status: string;
  isOffline: boolean;
  notes: string | null;
  budgetPeriodStartDate: string;
  budgetPeriodEndDate: string;
  discountAmt: number | string;
  salesTaxAmt: number | string;
  shipCostAmt: number | string;
  totalCostAmt: number | string;
  prepaid: boolean;
  isAutoDistGlBreakout: boolean;
  dateCompleted: string;
  apId: string;
  externalSupplier: string;
  attachments: InvoiceAttachment[];
  numOfAttachments: number;
  poStatus: string;
  canEditAttachments: boolean;
  isInvoiceNumberDuplicate: boolean;
  receivingQty: number;
  currentDocQty: number;
  triggerPreCheck: Trigger[];
  glBreakouts: InvoiceGlBreakoutItem[];
  lineItems: InvoiceLineItem[];
  isProcessingComplete: boolean;
  periodAllocations: InvoiceBudgetPeriodResponse[];
  isPoMpa?: boolean;
  supplierInheritance?: 'Inherited' | 'NotInherited' | null;
  periodGlCdAllocations: PeriodGLAllocation[];
  ocrConfidencePercentage: string;
}

export interface PeriodGLAllocation {
  glPeriodId: number;
  taxableAmount: number;
  glCdId: number;
  subTotal: number;
  discount: number;
  tax: number;
  shipping: number;
  isClosed?: number;
  startDate?: string;
  percentage: number;
}

export interface Trigger {
  messages: string[];
  preCheckResult: keyof typeof PreCheckResult;
  trigger: number;
}

const generatePeriodAllocations = (
  periodGLAllocations: PeriodGLAllocation[]
): InvoiceBudgetPeriodResponse[] => {
  let groupedByPeriods = Object.values(groupBy(periodGLAllocations, 'glPeriodId'));
  return groupedByPeriods.map((periodItem) => ({
    startDate: periodItem[0].startDate,
    isClosed: periodItem[0].isClosed,
    glPeriodId: periodItem[0].glPeriodId,
    percentage: periodItem[0].percentage,
    amount: round(
      sumBy(periodItem, 'subTotal') +
        sumBy(periodItem, 'tax') +
        sumBy(periodItem, 'shipping') -
        sumBy(periodItem, 'discount'),
      2
    ),
    percentageAmount: periodItem[0].percentage,
  }));
};

const useInvoiceDetailsApi = (invoiceId: string) => {
  const [invoiceBudgetPeriods, setInvoiceBudgetPeriods] = useState<IInvoiceBudgetPeriod[]>([]);
  const [initialInvoice, setInitialInvoice] = useState({} as InvoiceDetailsResponse);
  const [initialAPIDInvoice, setInitialAPIDInvoice] = useState('');
  const [initialInvoiceSupplierName, setInvoiceSupplierName] = useState('');
  const [invoiceGlCodes, setInvoiceGlCodes] = useState<InvoiceGlBreakoutItem[]>([]);
  const [invoiceLineItems, setInvoiceLineItems] = useState<InvoiceLineItem[]>([]);
  const [initialInvoiceLineItems, setInitialInvoiceLineItems] = useState<InvoiceLineItem[]>([]);

  const call = useCallback(async () => {
    try {
      const res = await axiosInstance.get<InvoiceDetailsResponse>(`Invoice/${invoiceId}`);
      res.data.periodGlCdAllocations = res.data.periodGlCdAllocations.map((al) => ({
        ...al,
        discount: al.discount === 0 ? 0 : -al.discount,
      }));
      res.data.periodAllocations = generatePeriodAllocations(res.data.periodGlCdAllocations);
      const response: InvoiceDetailsResponse = {
        ...res.data,

        invoiceDate: res.data.invoiceDate
          ? removeTime(new Date(res.data.invoiceDate))
          : res.data.invoiceDate,
        _invoiceDate: res.data.invoiceDate ? moment(res.data.invoiceDate).format('M/DD/YYYY') : '',
        receivedDate: res.data.receivedDate
          ? removeTime(new Date(res.data.receivedDate))
          : res.data.receivedDate,
        _receivedDate: res.data.receivedDate
          ? moment(res.data.receivedDate).format('M/DD/YYYY')
          : '',
        periodAllocations: updateAllocationDecimal(
          res.data.periodAllocations,
          Number(res.data.totalCostAmt)
        ).map((allocation) => ({
          ...allocation,
          percentageAmount: calculatePercentage(Number(res.data.totalCostAmt), allocation.amount),
        })),
        ocrConfidencePercentage: res.data.ocrConfidencePercentage
          ? `${res.data.ocrConfidencePercentage}%`
          : '0%',
      };
      setInitialAPIDInvoice(response.apId);
      setInvoiceSupplierName(response.externalSupplier);
      setInitialInvoice(response);
      setInvoiceBudgetPeriods(generateInitialAllocations(response));
      const invoiceGlCodes: InvoiceGlBreakoutItem[] = response?.glBreakouts?.map((item) => ({
        ...item,
        discountAmt: item.discountAmt * -1,
        glSubTotal: item.subTotalAmt,
        taxableAmount: item.taxableAmt,
        glTotal: round(
          item.subTotalAmt + item.discountAmt + item.salesTaxAmt + item.shipCostAmt,
          2
        ),
        rowId: uuid(),
      }));
      invoiceGlCodes.sort((a, b) => a.glCodeId - b.glCodeId);
      const oldGlCodes = invoiceGlCodes.filter((code) => !code.isNewlyAdded);
      const newGlCodes = invoiceGlCodes.filter((code) => code.isNewlyAdded);

      setInvoiceGlCodes([...oldGlCodes, ...newGlCodes, calculateTotals(invoiceGlCodes)]);
      return response as InvoiceDetailsResponse;
    } catch (error) {
      throw new Error(error);
    }
  }, [invoiceId]);

  const { value, setValue, execute, isLoading } = useAsync(
    call,
    true,
    undefined,
    {} as InvoiceDetailsResponse
  );

  const isInvoiceDirty = useMemo(() => !isEqual(initialInvoice, value), [initialInvoice, value]);
  const isInvoiceAPIDDirty = useMemo(
    () => !isEqual(initialAPIDInvoice, value.apId),
    [initialAPIDInvoice, value]
  );
  const isInvoiceSupplierNameDirty = useMemo(
    () => !isEqual(initialInvoiceSupplierName, value.externalSupplier),
    [initialInvoiceSupplierName, value]
  );

  const isInvoiceLineItemsDirty = useMemo(() => {
    if (value.poNum == null || invoiceLineItems.length)
      return !isEqual(initialInvoiceLineItems, invoiceLineItems);
    else return false;
  }, [initialInvoiceLineItems, invoiceLineItems, value.poNum]);

  return {
    invoice: value,
    setInvoice: setValue,
    getInvoice: execute,
    isInvoiceLoading: isLoading,
    initialInvoice,
    isInvoiceDirty,
    isInvoiceAPIDDirty,
    isInvoiceSupplierNameDirty,
    invoiceGlCodes,
    setInvoiceGlCodes,
    invoiceLineItems,
    setInvoiceLineItems,
    initialInvoiceLineItems,
    setInitialInvoiceLineItems,
    isInvoiceLineItemsDirty,
    invoiceBudgetPeriods,
    setInvoiceBudgetPeriods,
  };
};

export default useInvoiceDetailsApi;
